/* eslint-disable camelcase */
import React, { createContext } from 'react';
import { useFormik } from 'formik';
import Form from '../form-elements/Form';
import TextField from '../form-elements/TextField';
import TextArea from '../form-elements/TextArea';
import RadioGroup from '../form-elements/RadioGroup';
import Select from '../form-elements/Select';
import Required from '../form-elements/Required';
import Legend from '../form-elements/Legend';
import Picture from '../Picture';
import LoadingSpinner from '../LoadingSpinner';
import SuccessModal from '../form-elements/SuccessModal';
import ErrorBoundary from '../ErrorBoundary';
import SidebarContact from './elements/SidebarContact';
import OpeningTimes from './elements/OpeningTimes';
import useContactForm from './hooks/useContactForm';

export const FormContext = createContext(null);

const scrollToTop = () => {
  window.scrollTo(0, 0);
};

const ContactForm = () => {
  const {
    initialValues,
    validationSchema,
    disable,
    postForm,
    fields = {},
    waiting,
    handleReCaptchaVerify,
    showSuccessDialog,
    setShowSuccessDialog,
  } = useContactForm();

  const {
    handleBlur,
    touched,
    values,
    errors,
    setFieldValue,
    setValues,
    setTouched,
    submitForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async () => {
      scrollToTop();
      const recaptchaToken = await handleReCaptchaVerify();

      const formData = {
        ...values,
        'g-recaptcha-response': recaptchaToken,
      };

      await postForm(formData, recaptchaToken, () => {
        setValues(initialValues);

        setTouched({});

        // post to data layer
        const {
          first_name,
          last_name,
          phone,
          email,
          opt_in,
          current_page,
          referrer,
        } = values;

        // set email for DotDigital WBT
        window.dmPt('identify', email);

        const dataObj = {
          event: '2024_form_submit',
          data: {
            eventLabel: 'Contact',
            formType: 'contact',
            marketingConsent: opt_in,
            firstName: first_name,
            lastName: last_name,
            phone,
            email,
            currentPage: current_page,
            referrer: referrer,
          },
        };

        window.dataLayer.push(dataObj);
      });
    },
    enableReinitialize: true,
  });

  const {
    first_name,
    last_name,
    phone,
    email,
    purpose,
    how_hear,
    message,
    opt_in,
    honeypot,
    current_page,
    referrer,
    client_id,
  } = values;

  if (waiting) {
    return <LoadingSpinner />;
  } else {
    const { form, tour: tourMeta, intro, thank_you } = fields;

    const {
      first_name: firstNameMeta,
      last_name: lastNameMeta,
      phone: phoneMeta,
      email: emailMeta,
      purpose: purposeMeta,
      how_hear: howHearMeta,
      message: messageMeta,
      opt_in: optInMeta,
      gdpr: gdprMeta,
      submit: submitMeta,
    } = form;

    return (
      <FormContext.Provider value={fields.sidebar}>
        <section className="c-form c-form--contact o-wrapper">
          <div className="o-bg o-bg--centre">
            <div className="o-bg__overlay o-bg__overlay--white-thick"></div>
            {intro.image && <Picture image={intro.image} />}
          </div>

          <ErrorBoundary>
            <div className="c-form__main">
              <div className="c-intro__content">
                <div className="c-intro__content__text">
                  <h1 className="c-intro__content__text__title o-after-line o-after-line--purple">
                    {intro.title}
                  </h1>

                  <p
                    className="o-text"
                    dangerouslySetInnerHTML={{ __html: intro.content }}
                  ></p>
                </div>
              </div>

              <Form
                className="c-form__body"
                onSubmit={(e) => {
                  e.preventDefault();

                  submitForm();
                }}
              >
                {tourMeta && (
                  <div className="c-form__row">
                    <div className="c-form__item">
                      <TextField
                        label={
                          <>
                            Tour Name
                            <Required />
                          </>
                        }
                        name="tour_name"
                        placeholder="Tour Name"
                        value={tourMeta.display}
                        disabled={true}
                      />
                    </div>
                  </div>
                )}

                <p className="c-form__small u-mb-5">*Required Fields</p>

                <fieldset className="c-form__row">
                  <Legend>Name</Legend>

                  <label className="u-a11y">
                    Honeypot
                    <input
                      name="honeypot"
                      type="hidden"
                      onChange={() => setFieldValue('honeypot', true)}
                      value={honeypot}
                    />
                  </label>

                  <input name="referrer" value={referrer} type="hidden" />
                  <input name="client_id" value={client_id} type="hidden" />
                  <input
                    name="current_page"
                    value={current_page}
                    type="hidden"
                  />

                  <div className="c-form__item">
                    <TextField
                      label={
                        <>
                          {firstNameMeta.label}
                          {firstNameMeta.required && <Required />}
                        </>
                      }
                      name="first_name"
                      placeholder={firstNameMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) =>
                        setFieldValue('first_name', value)
                      }
                      value={first_name}
                      validationError={errors.first_name && touched.first_name}
                      validationMessage={errors.first_name}
                    />
                  </div>

                  <div className="c-form__item">
                    <TextField
                      label={
                        <>
                          {lastNameMeta.label}
                          {lastNameMeta.required && <Required />}
                        </>
                      }
                      name="last_name"
                      placeholder={lastNameMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) =>
                        setFieldValue('last_name', value)
                      }
                      value={last_name}
                      validationError={errors.last_name && touched.last_name}
                      validationMessage={errors.last_name}
                    />
                  </div>
                </fieldset>

                <fieldset className="c-form__row">
                  <Legend>Phone and email</Legend>

                  <div className="c-form__item">
                    <TextField
                      label={
                        <>
                          {phoneMeta.label}
                          {phoneMeta.required && <Required />}
                        </>
                      }
                      name="phone"
                      type="tel"
                      placeholder={phoneMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) => setFieldValue('phone', value)}
                      value={phone}
                      validationError={errors.phone && touched.phone}
                      validationMessage={errors.phone}
                    />
                  </div>

                  <div className="c-form__item">
                    <TextField
                      label={
                        <>
                          {emailMeta.label}
                          {emailMeta.required && <Required />}
                        </>
                      }
                      name="email"
                      type="email"
                      placeholder={emailMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) => setFieldValue('email', value)}
                      value={email}
                      validationError={errors.email && touched.email}
                      validationMessage={errors.email}
                    />
                  </div>
                </fieldset>

                <fieldset>
                  <Legend>Enquiry details</Legend>

                  <div className="c-form__row">
                    <div className="c-form__item">
                      <RadioGroup
                        name="purpose"
                        options={purposeMeta.options}
                        label={
                          <>
                            {purposeMeta.label}
                            {purposeMeta.required && <Required />}
                          </>
                        }
                        handleChange={(value) =>
                          setFieldValue('purpose', value)
                        }
                        value={purpose}
                      />
                    </div>

                    <div className="c-form__item">
                      <Select
                        options={howHearMeta.options.filter(
                          ({ disabled }) => !disabled
                        )}
                        value={how_hear}
                        id={howHearMeta.id}
                        label={
                          <>
                            {howHearMeta.label}
                            {howHearMeta.required && <Required />}
                          </>
                        }
                        placeholderMessage={howHearMeta.placeholder_option}
                        handleChange={(value) =>
                          setFieldValue('how_hear', value)
                        }
                      />
                    </div>
                  </div>

                  <div className="c-form__item c-form__item--enquiry">
                    <TextArea
                      label={
                        <>
                          {messageMeta.label}
                          {messageMeta.required && <Required />}
                        </>
                      }
                      name="enquiry"
                      placeholder={messageMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) => setFieldValue('message', value)}
                      value={message}
                      validationError={errors.message && touched.message}
                      validationMessage={errors.message}
                    />
                  </div>
                </fieldset>

                <fieldset className="c-form__item c-form__item--opt-in u-mb-3 opt-in">
                  <Legend>Opt in</Legend>

                  <label htmlFor="opt_in" className="c-form__checkbox">
                    <input
                      id="opt_in"
                      value="opt_in"
                      type="checkbox"
                      name="opt_in"
                      onChange={() => setFieldValue('opt_in', !opt_in)}
                      checked={opt_in}
                    />
                    <span></span>
                  </label>

                  <div>
                    <p className="u-weight--semibold u-mb-0">
                      {optInMeta.options[0].display}
                    </p>

                    <p
                      className="c-form__small"
                      dangerouslySetInnerHTML={{
                        __html: optInMeta.options[0].info,
                      }}
                    ></p>
                  </div>
                </fieldset>

                <div className="c-form__item c-form__item--gdpr-no-checkbox u-mb-3">
                  <div>
                    <p className="u-weight--semibold u-mb-0">
                      {gdprMeta.display}
                    </p>

                    <p
                      className="c-form__small"
                      dangerouslySetInnerHTML={{
                        __html: gdprMeta.info,
                      }}
                    ></p>
                  </div>
                </div>

                <div className="c-form__submit">
                  <button
                    type="submit"
                    className="o-btn o-btn--soft-sky"
                    disabled={disable(errors, values, initialValues)}
                  >
                    {submitMeta.label}
                  </button>
                </div>
              </Form>

              {showSuccessDialog && (
                <SuccessModal
                  thank_you={thank_you}
                  setShow={setShowSuccessDialog}
                />
              )}
            </div>
          </ErrorBoundary>

          <div className="c-form__sideinfo u-mb-10">
            <ErrorBoundary>
              <OpeningTimes />

              <SidebarContact />
            </ErrorBoundary>
          </div>
        </section>
      </FormContext.Provider>
    );
  }
};

export default ContactForm;
